import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../components/ui/dialog";

function KeyValue({ label, value, testId }) {
  return (
    <div className="grid grid-cols-[minmax(0,140px)_1fr] gap-3 py-2">
      <dt className="text-xs uppercase tracking-wide text-text-secondary">
        {label}
      </dt>
      <dd className="text-sm text-text-primary" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

export function ProductDetailModal({ product, open, onOpenChange }) {
  const { t } = useTranslation();
  const [imageIndex, setImageIndex] = useState(0);

  const images = useMemo(() => {
    if (!product) return [];
    if (Array.isArray(product.image_urls) && product.image_urls.length) {
      return product.image_urls;
    }
    return product.image_url ? [product.image_url] : [];
  }, [product]);

  useEffect(() => {
    if (open) setImageIndex(0);
  }, [open, product?.id]);

  const canNavigate = images.length > 1;
  const prev = useCallback(
    () => setImageIndex((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );
  const next = useCallback(
    () => setImageIndex((i) => (i + 1) % images.length),
    [images.length]
  );

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-detail-modal"
        className="max-w-4xl max-h-[90vh] gap-0 overflow-y-auto rounded-lg border-border-subtle bg-surface p-0 text-text-primary [&>button.absolute]:hidden"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Image */}
          <div className="relative aspect-square w-full overflow-hidden bg-offwhite md:aspect-auto">
            {images[imageIndex] ? (
              <img
                src={images[imageIndex]}
                alt={product.name}
                className="h-full w-full object-cover"
                data-testid="product-detail-image"
              />
            ) : (
              <div className="h-full w-full bg-offwhite" />
            )}

            {canNavigate && (
              <>
                <button
                  type="button"
                  aria-label={t("products.detail.prev_image")}
                  onClick={prev}
                  data-testid="product-detail-prev"
                  className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-surface/85 text-charcoal shadow-card transition hover:bg-surface"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={t("products.detail.next_image")}
                  onClick={next}
                  data-testid="product-detail-next"
                  className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-surface/85 text-charcoal shadow-card transition hover:bg-surface"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
                  {images.map((_, i) => (
                    <span
                      key={i}
                      aria-hidden
                      className={`h-1.5 w-1.5 rounded-full transition ${
                        i === imageIndex ? "bg-charcoal" : "bg-charcoal/25"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Details */}
          <div className="relative flex flex-col p-6 md:p-8">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label={t("common.close")}
              data-testid="product-detail-close"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-text-secondary transition hover:bg-offwhite hover:text-charcoal"
            >
              <X className="h-4 w-4" />
            </button>

            {product.brand && (
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-taupe">
                {product.brand}
              </div>
            )}
            <DialogTitle
              className="mt-1 pr-8 text-2xl font-semibold leading-tight tracking-tight"
              data-testid="product-detail-name"
            >
              {product.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {product.name}
              {product.category ? ` — ${product.category}` : ""}
            </DialogDescription>
            {product.category && (
              <div className="mt-1 text-xs text-text-secondary">
                {product.category}
              </div>
            )}

            <div className="mt-6 h-px w-full bg-border-subtle" />

            <dl className="mt-2 divide-y divide-border-subtle">
              <KeyValue
                label={t("products.in_stock")}
                value={t("products.in_stock_units", {
                  count: product.in_stock ?? 0,
                })}
                testId="product-detail-in-stock"
              />
              {product.category && (
                <KeyValue
                  label={t("products.detail.category")}
                  value={product.category}
                />
              )}
              {product.dimensions && (
                <KeyValue
                  label={t("products.detail.product_dimensions")}
                  value={product.dimensions}
                  testId="product-detail-dimensions"
                />
              )}
              {product.weight_kg != null && (
                <KeyValue
                  label={t("products.detail.weight")}
                  value={`${product.weight_kg} kg`}
                />
              )}
              {product.sku && (
                <KeyValue
                  label={t("admin.products.field_sku")}
                  value={product.sku}
                  testId="product-detail-sku"
                />
              )}
            </dl>

            {product.description && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-wide text-text-secondary">
                  {t("products.detail.description")}
                </div>
                <p
                  className="mt-2 whitespace-pre-line text-sm leading-relaxed text-text-primary"
                  data-testid="product-detail-description"
                >
                  {product.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
